import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';
import { Plus, UserPlus, UserMinus, Copy, Users, Key } from 'lucide-react';

/**
 * MyGroupsPanel
 * Drop-in card for managing a user's accountability/prayer groups.
 * All groups are invite-only — users create groups (which auto-generate an
 * 8-char invite code) and join other groups by entering that code.
 *
 * Reused on /gamification (Groups tab) and /prayers (Groups tab).
 */
export const MyGroupsPanel = ({ title = 'My Groups', description = "Groups you're a member of" }) => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();

  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);

  const fetchMyGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups/my`);
      setMyGroups(res.data || []);
    } catch (e) {
      console.error('MyGroupsPanel: failed to load groups', e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchMyGroups();
  }, [fetchMyGroups]);

  const handleCreate = async () => {
    const name = newGroupName.trim();
    if (!name) {
      toast.error('Group name is required');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/groups`, {
        name,
        description: newGroupDescription.trim() || undefined,
        invite_only: true,
      });
      toast.success(`Group created! Invite code: ${res.data.invite_code}`, { duration: 8000 });
      setNewGroupName('');
      setNewGroupDescription('');
      setCreateDialogOpen(false);
      fetchMyGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) {
      toast.error('Enter an invite code');
      return;
    }
    setJoining(true);
    try {
      const res = await axios.post(`${API}/groups/join-by-code`, { invite_code: code });
      toast.success(`Joined ${res.data.name}!`);
      setJoinCode('');
      setJoinDialogOpen(false);
      fetchMyGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to join group');
    } finally {
      setJoining(false);
    }
  };

  const handleLeave = async (groupId) => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    try {
      await axios.post(`${API}/groups/${groupId}/leave`);
      toast.success('Left group');
      fetchMyGroups();
    } catch (e) {
      toast.error('Failed to leave group');
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Invite code ${code} copied`);
    } catch (e) {
      toast.error('Could not copy — long-press to copy manually');
    }
  };

  return (
    <Card data-testid="my-groups-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-[#2B6B7F] flex items-center gap-2">
              <Users className="w-5 h-5" />
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={joinDialogOpen} onOpenChange={setJoinDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#2B6B7F] text-[#2B6B7F]"
                  data-testid="open-join-group-dialog"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Join by Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Join a Group</DialogTitle>
                  <DialogDescription>
                    Enter the 8-character invite code shared by the group&apos;s creator.
                  </DialogDescription>
                </DialogHeader>
                <Input
                  placeholder="e.g. AB12CD34"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  maxLength={12}
                  className="uppercase tracking-widest font-mono"
                  data-testid="join-group-code-input"
                />
                <Button
                  onClick={handleJoin}
                  disabled={joining}
                  className="w-full bg-[#2B6B7F]"
                  data-testid="join-group-submit"
                >
                  {joining ? 'Joining…' : 'Join Group'}
                </Button>
              </DialogContent>
            </Dialog>

            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#2B6B7F]" data-testid="open-create-group-dialog">
                  <Plus className="w-4 h-4 mr-1" />
                  Create
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create a Group</DialogTitle>
                  <DialogDescription>
                    Groups are private and invite-only. You&apos;ll get an 8-character invite
                    code to share with the people you want to add.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <Input
                    placeholder="Group name"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    data-testid="create-group-name-input"
                  />
                  <Textarea
                    placeholder="Description (optional)"
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    rows={3}
                    data-testid="create-group-description-input"
                  />
                  <Button
                    onClick={handleCreate}
                    disabled={creating}
                    className="w-full bg-[#2B6B7F]"
                    data-testid="create-group-submit"
                  >
                    {creating ? 'Creating…' : 'Create Group'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500 text-center py-4">Loading groups…</p>
        ) : myGroups.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6" data-testid="no-groups-empty-state">
            You haven&apos;t joined any groups yet. Create one or join by code.
          </p>
        ) : (
          <div className="space-y-3">
            {myGroups.map((group) => {
              const isOwner = user && group.created_by === user.id;
              return (
                <div
                  key={group.id}
                  className="p-3 rounded-lg border border-slate-200 hover:border-[#2B6B7F]/50 transition-colors"
                  data-testid={`group-row-${group.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/groups/${group.id}`)}
                      className="min-w-0 flex-1 text-left"
                      data-testid={`open-group-${group.id}`}
                    >
                      <p className="font-semibold text-[#2B6B7F] hover:underline truncate">
                        {group.name}
                      </p>
                      {group.description && (
                        <p className="text-xs text-slate-500 truncate">{group.description}</p>
                      )}
                      <p className="text-xs text-slate-500 mt-1">
                        {(group.members?.length ?? group.member_count ?? 0)} members
                        {isOwner && ' • You created this'}
                      </p>
                    </button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleLeave(group.id)}
                      data-testid={`leave-group-${group.id}`}
                      title="Leave group"
                    >
                      <UserMinus className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>

                  {isOwner && group.invite_code && (
                    <button
                      type="button"
                      onClick={() => copyCode(group.invite_code)}
                      className="mt-2 w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      data-testid={`group-invite-code-${group.id}`}
                    >
                      <span className="flex items-center gap-2 text-xs text-slate-600">
                        <Key className="w-3 h-3" />
                        Invite code
                      </span>
                      <span className="font-mono font-semibold tracking-widest text-[#2B6B7F] flex items-center gap-1.5">
                        {group.invite_code}
                        <Copy className="w-3 h-3 opacity-60" />
                      </span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyGroupsPanel;
